<template>
  <div ref="rootRef" class="relative">
    <input
      v-model="q"
      type="search"
      placeholder="Rechercher…"
      class="w-40 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-space-indigo placeholder:text-slate-400 focus:w-56 focus:outline-none focus:ring-2 focus:ring-altoneo-300 dark:border-alt-dark-border dark:bg-alt-dark-surface dark:text-altoneo-50 sm:w-48"
      @input="onInput"
      @focus="open = true"
    />

    <div
      v-if="open && q.trim().length >= 2"
      class="absolute right-0 z-50 mt-2 max-h-[70vh] w-80 overflow-auto rounded-xl border border-slate-200 bg-white p-2 shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
    >
      <p v-if="loading" class="px-2 py-3 text-center text-xs text-slate-400">Recherche…</p>
      <p v-else-if="empty" class="px-2 py-3 text-center text-xs text-slate-400">Aucun résultat pour « {{ q }} ».</p>
      <template v-else>
        <SearchGroup label="Projets" :hits="results.projects" @go="(h) => goto('project', h)" />
        <SearchGroup label="Actions" :hits="results.actions" @go="(h) => goto('action', h)" />
        <SearchGroup label="Tâches" :hits="results.tasks" @go="(h) => goto('task', h)" />
        <SearchGroup label="Idées" :hits="results.ideas" @go="(h) => goto('idea', h)" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { SearchHit, SearchResults } from '@its/shared';
import { api } from '@/lib/api';
import SearchGroup from '@/components/SearchGroup.vue';

const router = useRouter();
const q = ref('');
const open = ref(false);
const loading = ref(false);
const results = ref<SearchResults>({ projects: [], actions: [], tasks: [], ideas: [] });
const rootRef = ref<HTMLElement | null>(null);
let timer: ReturnType<typeof setTimeout> | null = null;
let seq = 0;

const empty = computed(
  () =>
    results.value.projects.length === 0 &&
    results.value.actions.length === 0 &&
    results.value.tasks.length === 0 &&
    results.value.ideas.length === 0,
);

function onInput(): void {
  open.value = true;
  if (timer) clearTimeout(timer);
  const term = q.value.trim();
  if (term.length < 2) {
    results.value = { projects: [], actions: [], tasks: [], ideas: [] };
    return;
  }
  loading.value = true;
  timer = setTimeout(async () => {
    const mine = ++seq;
    try {
      const { results: r } = await api.get<{ results: SearchResults }>(`/search?q=${encodeURIComponent(term)}`);
      if (mine === seq) results.value = r; // ignore les réponses hors d'ordre
    } catch {
      if (mine === seq) results.value = { projects: [], actions: [], tasks: [], ideas: [] };
    } finally {
      if (mine === seq) loading.value = false;
    }
  }, 250);
}

function goto(kind: 'project' | 'action' | 'task' | 'idea', h: SearchHit): void {
  open.value = false;
  q.value = '';
  if (kind === 'idea') void router.push('/ideation');
  else if (kind === 'project') void router.push(`/projets/${h.id}`);
  else if (h.projectId) void router.push(`/projets/${h.projectId}`);
}

function onDocClick(e: MouseEvent): void {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target as Node)) open.value = false;
}
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>
